import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import classNames from 'classnames';

import ImageProxy from '@next-image-proxy';

import { Button, ExternalLink } from '@uikit';

import TeamMemberList from '@launchpad/components/common/TeamMemberList/TeamMemberList';

import type { ProjectContentTableOfContentTab } from '../types';
import type { ItemsProps } from '../types';
import {
  businessModel,
  investors,
  keyFeatures,
  marketTraction,
  participationCriteria,
  partners,
  teamMembers,
  tokenUtility,
} from './contants';

export interface SubTitleProps extends ComponentPropsWithoutRef<'h3'> {
  children: ReactNode;
}

export interface ListProps extends ComponentPropsWithoutRef<'ul'> {
  items: Array<ItemsProps>;
  description?: string;
  descriptionClassName?: string;
  withNumericSymbols?: boolean;
}

const List = ({
  items,
  description,
  className,
  descriptionClassName,
  withNumericSymbols,
}: ListProps) => {
  return (
    <>
      {Boolean(description) && (
        <p
          className={classNames(
            'mb-4 sm:text-2xl font-semibold text-neutral-500',
            descriptionClassName,
          )}
        >
          {description}
        </p>
      )}
      <ul
        className={classNames(
          'pl-6',
          {
            'list-decimal': withNumericSymbols,
            'list-disc': !withNumericSymbols,
          },
          className,
        )}
      >
        {items.map(({ id, content, item }) => {
          if (Array.isArray(item)) {
            return (
              <li key={id} className="mb-4">
                {content}
                <ul className="list-disc pl-6">
                  {item.map(({ id: subId, subItem }) => (
                    <li key={subId}>{subItem}</li>
                  ))}
                </ul>
              </li>
            );
          } else {
            return (
              <li className="max-sm:text-sm" key={id}>
                {item}
              </li>
            );
          }
        })}
      </ul>
    </>
  );
};

export const tableOfContentTab: ProjectContentTableOfContentTab['content'] = [
  {
    id: 'pims-ido-participation-criteria',
    title: '$PIMS IDO Participation Criteria',
    content: (
      <>
        <p className="mb-4 max-sm:text-sm font-normal">To join the IDO, you have three options:</p>
        <List items={participationCriteria} withNumericSymbols />
        <p className="mt-4 max-sm:text-sm font-normal">
          If you qualify for more than one of the above, you can participate in multiple pools,
          increasing your allocation.
        </p>
      </>
    ),
  },
  {
    id: 'exclusive-giveaway',
    title: '$25,000 Exclusive Giveaway',
    content: (
      <>
        <p className="max-sm:text-sm font-normal">
          IDO participants who contribute $100 or more and do not request a refund post-$PIMS TGE
          will be eligible for a $25,000 $PIMS giveaway.
          <ExternalLink
            href="https://soquest.xyz/space/MagicSquare/campaign/a6YhrRbiHK"
            className="mx-1 text-base-text-blue underline"
          >
            Join here.
          </ExternalLink>
          100 winners will be selected to receive $250 worth of $PIMS each, in addition to their IDO
          allocation.
        </p>
      </>
    ),
  },
  {
    id: 'intro',
    title: 'Introduction',
    content: (
      <>
        <p className="max-sm:text-sm font-normal">
          Pinkmoon Studios is at the forefront of transforming the traditional MMO industry by
          building player-owned economies in Web3. Their flagship game, KMON: World of Kogaea,
          offers an open-world MMORPG experience with Pokémon-like elements. The company’s
          proprietary Mythos Engine drastically reduces MMO development time and costs, enabling
          Web3 and Web2 developers to create immersive games efficiently. Pinkmoon Studios is
          innovating both on the game development side and in offering a blockchain-driven ecosystem
          where players have true ownership of in-game assets.
        </p>
      </>
    ),
  },
  {
    id: 'key-features',
    title: 'Key Features',
    content: <List items={keyFeatures} />,
  },
  {
    id: 'problem-solution',
    title: 'Problem and Solution',
    content: (
      <>
        <p className="font-normal max-sm:text-sm">
          Traditional MMO games require immense development time, technical expertise, and
          resources, typically spanning 5-7 years. With Mythos Engine, Pinkmoon Studios solves this
          problem by offering a No-Code platform that accelerates the development process,
          drastically reducing time and costs. Additionally, traditional gaming lacks player
          ownership of assets. Pinkmoon changes that by using blockchain technology to allow true
          ownership through NFTs. Players can participate in the economy, trade assets, and engage
          in governance, creating a more equitable and engaging gaming experience.
        </p>
        <Button
          variant="secondary"
          className="block mt-6 max-sm:mt-4 mx-auto !py-2 px-6 max-sm:w-full rounded-lg"
        >
          <ExternalLink href="https://academy.kryptomon.co/the-kryptomon-academy-1">
            Learn More
          </ExternalLink>
        </Button>
      </>
    ),
  },
  {
    id: 'market-traction-achievements',
    title: 'Market Traction / Achievements',
    content: <List items={marketTraction} />,
  },
  {
    id: 'investors',
    title: 'Investors',
    content: (
      <>
        <ul className="flex items-start justify-around flex-wrap gap-5">
          {investors.map(({ key, name, image, width, height, className }) => {
            return (
              <li key={key} className={classNames('max-w-[180px] min-w-[100px]', className)}>
                <ImageProxy
                  alt={key}
                  width={width}
                  height={height}
                  quality={80}
                  className="w-full h-full object-contain"
                  src={image}
                />
                <span className="block mt-2 whitespace-nowrap">{name}</span>
              </li>
            );
          })}
        </ul>
      </>
    ),
  },
  {
    id: 'partners',
    title: 'Partners',
    content: (
      <>
        <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-1.5">
          {partners.map(({ name, image, width, height, className }) => {
            return (
              <li
                key={name}
                className={classNames('h-[4.5rem] rounded-lg border border-primary-200', className)}
              >
                <ImageProxy
                  alt={name}
                  width={width}
                  height={height}
                  quality={80}
                  className="w-full h-full object-contain"
                  src={image}
                />
              </li>
            );
          })}
        </ul>
      </>
    ),
  },
  {
    id: 'business-model',
    title: 'Business Model',
    content: <List items={businessModel} />,
  },
  {
    id: 'token-utility',
    title: 'Token Utility',
    content: (
      <>
        <p className="mb-3 font-normal max-sm:text-sm">
          {`The $PIMS token is integral to Pinkmoon Studio's game ecosystem and drives the platform’s
          economic model. Key utilities include:`}
        </p>
        <List items={tokenUtility} />
      </>
    ),
  },
  {
    id: 'token-metrics',
    title: 'Token Metrics',
    content: (
      <>
        <ImageProxy
          alt="Tokenomics diagram"
          src="/launchpad/stories/pink-moon/tokenomics.png"
          className="block w-full h-auto rounded-lg object-contain shadow-main"
          quality={80}
          width={774}
          height={435}
        />
        <Button
          variant="secondary"
          className="block mt-6 max-sm:mt-4 mx-auto !py-2 px-6 max-sm:w-full rounded-lg"
        >
          <ExternalLink className="underline" href="https://pink-moon.studio/pims">
            Learn More
          </ExternalLink>
        </Button>
      </>
    ),
  },
  {
    id: 'team',
    title: 'Team',
    content: (
      <>
        <TeamMemberList
          className="!grid-cols-1 !items-start max-md:flex-col"
          cardClassName="!items-start max-md:flex-col"
          teamMembers={teamMembers}
        />
      </>
    ),
  },
  {
    id: 'roadmap',
    title: 'Roadmap',
    content: (
      <>
        <ImageProxy
          alt="Roadmap"
          src="/launchpad/stories/pink-moon/roadmap.png"
          className="block w-full h-auto rounded-lg object-contain shadow-main"
          quality={80}
          width={774}
          height={447}
        />
      </>
    ),
  },
];
